import { DEFAULT_GROUP_DATA } from 'constants';
import { groupsService, clientsCentralSearchService, groupsV2Service } from 'config/api';
import { addNotification } from 'shell/actions';
import omit from 'lodash/omit';
import isEmpty from 'lodash/isEmpty';
import { mapFilteredParams, fillEmptyLetters } from 'modules/clients/utils';

import actionTypes from './actionTypes';

export const clearClientsCache = () => (dispatch) => {
  dispatch({ type: actionTypes.CLIENTS_INFINITE_LIST_CLEAR });
};

export const resetClientsGroups = () => (dispatch) => {
  dispatch({ type: actionTypes.RESET_CLIENT_GROUPS });
};

export const fetchClientGroups = ({ includeStats, isDisabled, isDefaultGroupsOnly, isApiV2 = false } = {}) => async (
  dispatch,
) => {
  dispatch({ type: actionTypes.GET_GROUPS_LOADING });

  try {
    if (isApiV2) {
      // API version 2
      const params = {};

      if (isDefaultGroupsOnly) {
        params.defaultOnly = true;
      }

      const res = await groupsV2Service.get('/groups', { params });
      const groups = Array.isArray(res.data) ? res.data : [];

      dispatch({ type: actionTypes.GET_GROUPS_SUCCESS, payload: { groups, isDefaultGroupsOnly, isApiV2: true } });
    } else {
      // API version 1
      const res = !isDisabled ? await groupsService.get('/groups') : { data: [] };
      const groups = Array.isArray(res.data) ? res.data : [];
      let defaultStats = {};

      if (includeStats) {
        const defaultStatsRes = await clientsCentralSearchService.get(`/myCustomers?statsOnly=true`);
        defaultStats = defaultStatsRes.data;
      }

      dispatch({ type: actionTypes.GET_GROUPS_SUCCESS, payload: { groups, defaultStats, isApiV2: false } });
    }
  } catch (err) {
    const error = err.response?.data?.message || err.message;
    dispatch({ type: actionTypes.GET_GROUPS_ERROR, payload: error });
  }
};

export const fetchGroupsByClientId = (clientId) => async (dispatch) => {
  dispatch({ type: actionTypes.GET_CLIENT_GROUPS_LOADING });

  try {
    const res = await groupsService.get(`/groupsByCustomer/${clientId}`);

    const groups = Array.isArray(res.data) ? res.data : [];

    dispatch({ type: actionTypes.GET_CLIENT_GROUPS_SUCCESS, payload: groups });
  } catch (err) {
    const error = err.response?.data?.message || err.message;
    dispatch({ type: actionTypes.GET_CLIENT_GROUPS_ERROR, payload: error });
  }
};

const fetchGroupClientsCall = async (groupId, params, returnFacets = false) => {
  const filteredParams = mapFilteredParams(params, returnFacets);
  const {
    data: { data: resData },
  } = await groupsV2Service.get(`/groups/${groupId}/customers`, { params: filteredParams });

  if (returnFacets) {
    return resData;
  }

  const stats = {};

  if (resData.total !== undefined) {
    stats.totalCount = resData.total;
  }

  if (resData.alphabet) {
    stats.alphabeticalMap = fillEmptyLetters(resData.alphabet);
  } else {
    stats.alphabeticalMap = {};
  }

  const customers = {};

  // Customers array is transformed into map with relevant elements positions
  // (because we can load customers from 0 to 30, and after it from 100 to 120, and they should be saved appropriately
  for (let i = params.from, j = 0; i <= params.from + params.size; i += 1, j += 1) {
    if (resData.customers[j]) {
      customers[i] = resData.customers[j];
    }
  }

  return { customers, stats };
};

export const fetchGroupFilterFacets = (groupId, params) => async (dispatch) => {
  dispatch({ type: actionTypes.CLIENTS_FILTER_FACETS_LOADING });
  try {
    const data = await fetchGroupClientsCall(groupId, params, true);
    dispatch({ type: actionTypes.CLIENTS_FILTER_FACETS_SUCCESS, payload: data?.facets });
  } catch (error) {
    dispatch({
      type: actionTypes.CLIENTS_FILTER_FACETS_ERROR,
      payload:
        error.response?.data?.message || error?.message || 'Problems with filter loading, please try again later.',
    });
  }
};

export const fetchGroupClients = (
  groupId,
  params = {
    from: 0,
    size: 24,
  },
) => async (dispatch, getState) => {
  const currentState = getState();
  if (isEmpty(currentState.clientsModule.clientsList.clients)) {
    dispatch({ type: actionTypes.CLIENTS_INFINITE_LIST_LOADING, data: true });
  }

  // filter parameters with empty values ("0" is an exception)
  const customerFilterParams = {
    ...omit(params, ['from', 'size', 'departments', 'designers']),
    facets: {
      loading: false,
      data: {
        departments: params?.departments || [],
        designers: params?.designers || [],
      },
    },
  };

  try {
    const { customers, stats } = await fetchGroupClientsCall(groupId, params);

    dispatch({ type: actionTypes.CLIENTS_INFINITE_LIST_SUCCESS, data: { customers, showAddProfileButton: false } });
    dispatch({
      type: actionTypes.CLIENTS_INFINITE_LIST_STATS,
      data: {
        stats,
        customerFilterParams,
      },
    });

    return customers;
  } catch (error) {
    dispatch({
      type: actionTypes.GET_GROUP_CLIENTS_ERROR,
      payload:
        error.response?.data?.message ||
        error?.message ||
        'Not able to get client central list, please try again later.',
    });
  }
};

export const createNewGroup = (group) => async (dispatch) => {
  dispatch({ type: actionTypes.CREATE_GROUP_LOADING });

  try {
    const body = {
      name: group.name,
      description: group.description,
      coverColor: group.coverColor,
    };

    if (group.coverImageUrl) {
      body.coverImageUrl = group.coverImageUrl;
    }
    const { data } = await groupsV2Service.post('/groups', body);

    await groupsV2Service.post(`groups/${data.groupId}/customers`, group.clientIds);

    return {
      result: 'success',
      data,
    };
  } catch (err) {
    const error = err.response?.data?.message || 'Group creation error';
    dispatch({ type: actionTypes.CREATE_GROUP_ERROR, payload: error });
    return {
      result: 'error',
      err,
    };
  }
};

export const editGroup = (group) => async (dispatch) => {
  dispatch({ type: actionTypes.EDIT_GROUP_LOADING });

  try {
    const body = {
      groupId: group.id,
      name: group.name,
      description: group.description,
      coverColor: group.coverColor,
    };

    if (group.coverImageUrl) {
      body.coverImageUrl = group.coverImageUrl;
    }

    const response = await groupsV2Service.put(`/groups/${group.id}`, body);

    dispatch({ type: actionTypes.EDIT_GROUP_SUCCESS, payload: response.data });
    return 'success';
  } catch (err) {
    const error = err.response?.data?.message || 'Group edition error';
    dispatch({ type: actionTypes.EDIT_GROUP_ERROR, payload: error });
    return 'error';
  }
};

export const editGroupCover = (color) => (dispatch) => {
  dispatch({ type: actionTypes.EDIT_GROUP_COVER_LOADING });

  const hexColorRegex = /^#(?:[0-9a-f]{3}){1,2}$/i;

  if (color && hexColorRegex.test(color)) {
    dispatch({ type: actionTypes.EDIT_GROUP_COVER_SUCCESS, payload: color });
  } else {
    dispatch({ type: actionTypes.EDIT_GROUP_COVER_ERROR, payload: 'Wrong color hex code' });
  }
};

export const editGroupCoverReset = () => ({ type: actionTypes.EDIT_GROUP_COVER_MODE_RESET });

export const fetchGroupDetails = ({ groupId, isApiV2 = false }) => async (dispatch) => {
  dispatch({ type: actionTypes.GROUP_DETAILS_LOADING });

  try {
    if (isApiV2) {
      // API version 2
      try {
        const res = await groupsV2Service.get(`/groups/${groupId}`);

        dispatch({ type: actionTypes.GROUP_DETAILS_SUCCESS, payload: res.data });
      } catch (err) {
        const error = err.response?.data?.message || err.message;
        dispatch({ type: actionTypes.GROUP_DETAILS_ERROR, payload: error });
      }
    } else {
      // API version 1
      let groupDetails;

      if (groupId === DEFAULT_GROUP_DATA.groupId) {
        const res = await clientsCentralSearchService.get(`/myCustomers?statsOnly=true`);
        groupDetails = Object.assign({}, DEFAULT_GROUP_DATA, res.data);
      } else {
        const res = await groupsService.get(`/groups/${groupId}`);
        groupDetails = res.data;
      }

      dispatch({ type: actionTypes.GROUP_DETAILS_SUCCESS, payload: groupDetails });
    }
  } catch (err) {
    const error = err.response?.data?.message || err.message;
    dispatch({ type: actionTypes.GROUP_DETAILS_ERROR, payload: error });
  }
};

export const groupDetailsReset = () => ({ type: actionTypes.GROUP_DETAILS_RESET });

export const deleteGroup = (groupId) => async (dispatch) => {
  dispatch({ type: actionTypes.DELETE_GROUP_LOADING });

  try {
    await groupsV2Service.delete(`/groups/${groupId}`);
    dispatch({ type: actionTypes.DELETE_GROUP_SUCCESS });
    return 'success';
  } catch (err) {
    const error = err.response?.data?.message || 'Group delete error';
    dispatch({ type: actionTypes.DELETE_GROUP_ERROR, payload: error });
    return 'error';
  }
};

export const addToGroup = (groupId, clientIds) => async (dispatch) => {
  dispatch({ type: actionTypes.ADD_TO_GROUP_LOADING });

  try {
    await groupsV2Service.post(`groups/${groupId}/customers`, clientIds);

    dispatch({ type: actionTypes.ADD_TO_GROUP_SUCCESS });

    return 'success';
  } catch (err) {
    const error = err.response?.data?.message || 'Add to group error';
    dispatch({ type: actionTypes.ADD_TO_GROUP_ERROR, payload: error });
    return 'error';
  }
};

export const addGroupToProfile = (group) => ({
  type: actionTypes.ADD_GROUP_TO_PROFILE,
  payload: { group },
});

export const deleteFromGroup = (groupId, clientIds) => async (dispatch) => {
  dispatch({ type: actionTypes.DELETE_FROM_GROUP_LOADING });

  try {
    await groupsV2Service.delete(`groups/${groupId}/customers`, { data: clientIds });

    dispatch({ type: actionTypes.DELETE_FROM_GROUP_SUCCESS, payload: { clientIds, groupId } });
    dispatch(
      addNotification({
        id: `delete-${Date.now()}`,
        message: `${clientIds.length} person${clientIds.length > 1 ? 's' : ''} removed from group`,
        type: 'success',
      }),
    );
    return 'success';
  } catch (err) {
    const error = err.response?.data?.message || 'Delete from group error';
    dispatch({ type: actionTypes.DELETE_FROM_GROUP_ERROR, payload: error });
    return 'error';
  }
};

export const groupsSortBy = (sortBy) => ({
  type: actionTypes.GROUPS_SORT_BY,
  payload: sortBy,
});
